'use client';

import { createContext, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { NextIntlClientProvider } from 'next-intl';
import defaultMessages from '../messages/vn.json';
import { useIdPath } from './IdPathProvider';
import projectServices from "@/utils/services/project";
import languageService from "@/templates/Heritage/utils/services/language";

interface LanguageContextType {
    locale: string;
    rawLanguage?: string;
    setLocale: (locale: string) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

type Messages = { [key: string]: string | Messages };

import { isLocale } from '@/utils/constant/locales';

const localMessageLoaders: Record<string, () => Promise<{ default: Messages }>> = {
    en: () => import('../messages/en.json'),
    vn: () => import('../messages/vn.json'),
};

export const normalizeUiLocale = (val?: string | null): string => {
    if (!val) return 'vn';
    const str = String(val).trim().toLowerCase();
    if (['en', 'eng', 'us', 'gb', 'en-us', 'en-gb'].includes(str)) return 'en';
    if (['vn', 'vi', 'vie', 'vi-vn'].includes(str)) return 'vn';
    if (['ja', 'jp', 'jpn'].includes(str)) return 'ja';
    if (['ko', 'kr', 'kor'].includes(str)) return 'ko';
    if (['zh', 'cn', 'zho', 'chi', 'zh-cn', 'zh-tw'].includes(str)) return 'zh';
    if (['de', 'de-de'].includes(str)) return 'de';
    if (['fr', 'fr-fr'].includes(str)) return 'fr';
    if (['es', 'es-es'].includes(str)) return 'es';
    if (['km', 'khm'].includes(str)) return 'km';
    if (isLocale(str)) return str;
    return 'vn';
};

const unflattenMessages = (input: Messages): Messages => {
    if (!input || typeof input !== 'object') return input;
    const output: Messages = {};

    const assignDeep = (target: Messages, path: string[], value: any) => {
        let current: any = target;
        for (let i = 0; i < path.length - 1; i++) {
            const key = path[i];
            if (key === '__proto__' || key === 'constructor' || key === 'prototype') return;
            if (!current[key] || typeof current[key] !== 'object' || Array.isArray(current[key])) {
                current[key] = {};
            }
            current = current[key];
        }
        const lastKey = path[path.length - 1];
        if (lastKey === '__proto__' || lastKey === 'constructor' || lastKey === 'prototype') return;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            current[lastKey] = unflattenMessages(value as Messages);
        } else {
            current[lastKey] = value;
        }
    };

    Object.entries(input).forEach(([key, value]) => {
        if (key.includes('.')) {
            assignDeep(output, key.split('.'), value);
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            output[key] = unflattenMessages(value as Messages);
        } else {
            output[key] = value;
        }
    });

    return output;
};

export const mergeMessages = (base: Messages, override: Messages): Messages => {
    const result: Messages = { ...base };

    Object.entries(override || {}).forEach(([key, value]) => {
        const baseValue = result[key];
        if (
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            baseValue &&
            typeof baseValue === 'object' &&
            !Array.isArray(baseValue)
        ) {
            result[key] = mergeMessages(baseValue as Messages, value as Messages);
        } else {
            result[key] = value as string | Messages;
            if (typeof key === 'string' && key !== key.toUpperCase()) {
                result[key.toUpperCase()] = value as string | Messages;
            }
        }
    });

    return result;
};

const loadRuntimeMessages = async (
    locale: string,
    scope = 'global',
    projectId?: string
): Promise<Messages | null> => {
    try {
        const params = new URLSearchParams({
            locale,
            scope,
        });

        if (projectId) {
            params.set('project_id', projectId);
            params.set('id_path', projectId);
        }

        const response = await fetch(`/api/proxy/v1/general/languages/messages?${params.toString()}`, {
            cache: 'no-store',
        });

        if (!response.ok) return null;

        const payload = await response.json();
        return payload?.success && payload?.data?.messages ? payload.data.messages : null;
    } catch (error) {
        console.error(`Failed to load runtime messages for ${locale}:`, error);
        return null;
    }
};

const loadRuntimeMessageScopes = async (locale: string, projectId?: string): Promise<Messages | null> => {
    const scopes = projectId
        ? [
            { scope: 'global', projectId: undefined },
            { scope: 'menu', projectId },
            { scope: 'login', projectId },
        ]
        : [{ scope: 'global', projectId: undefined }];

    const scopedMessages = await Promise.all(
        scopes.map(({ scope, projectId: scopedProjectId }) => loadRuntimeMessages(locale, scope, scopedProjectId))
    );

    return scopedMessages.reduce<Messages | null>((mergedMessages, messages) => {
        if (!messages) return mergedMessages;
        return mergedMessages ? mergeMessages(mergedMessages, messages) : messages;
    }, null);
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within I18nProvider');
    }
    return context;
};

// Hàm load messages từ file local và runtime API
export const loadMessages = async (locale: string, projectId?: string): Promise<Messages> => {
    const uiLocale = normalizeUiLocale(locale);
    try {
        const localMessages = localMessageLoaders[uiLocale]
            ? (await localMessageLoaders[uiLocale]()).default
            : {};
        const runtimeMessages = await loadRuntimeMessageScopes(uiLocale, projectId);
        if (runtimeMessages) {
            return mergeMessages(defaultMessages, mergeMessages(localMessages, runtimeMessages));
        }
        return mergeMessages(defaultMessages, localMessages);
    } catch (error) {
        console.error(`Failed to load messages for ${locale} (normalized: ${uiLocale}):`, error);
        // Fallback về tiếng Việt nếu không tìm thấy file
        const runtimeMessages = await loadRuntimeMessageScopes(uiLocale, projectId);
        if (runtimeMessages) {
            return mergeMessages(defaultMessages, runtimeMessages);
        }
        return defaultMessages;
    }
};

export const I18nProvider = ({ children }: { children: React.ReactNode }) => {
    const [locale, setLocale] = useState('vn');
    const [rawLanguage, setRawLanguage] = useState('vn');
    const [messages, setMessages] = useState<Messages>(defaultMessages);
    const { idPath, isLoading } = useIdPath();
    const activeRequestIdRef = useRef(0);

    useEffect(() => {
        if (isLoading) return;

        // Ưu tiên lấy từ URL (query parameter)
        const params = new URLSearchParams(window.location.search);
        let langFromUrl = params.get('lang');
        let isRootPath = false;
        
        // Nếu không có trong query, lấy từ segment đầu tiên của pathname
        if (!langFromUrl && typeof window !== 'undefined') {
            const pathname = window.location.pathname;
            const segments = pathname.split('/').filter(Boolean);
            if (segments.length > 0) {
                const firstSegment = segments[0].toLowerCase();
                if (isLocale(firstSegment)) {
                    langFromUrl = firstSegment;
                }
            } else {
                // Trực tiếp truy cập trang gốc root "/" (không có prefix ngôn ngữ)
                isRootPath = true;
            }
        }

        // Lấy từ cookie 'language' (do middleware set khi redirect)
        let langFromCookie = null;
        if (typeof document !== 'undefined') {
            const match = document.cookie.match(new RegExp('(^| )language=([^;]+)'));
            if (match) {
                langFromCookie = match[2];
            }
        }

        const storedLang = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
        const reqId = ++activeRequestIdRef.current;

        getLanguageDefault().then(async (defaultLang) => {
            if (reqId !== activeRequestIdRef.current) return;
            let finalLang: string;

            if (idPath !== "") {
                // Trang dự án cụ thể
                const candidateLang = langFromUrl || langFromCookie || storedLang;
                if (candidateLang) {
                    try {
                        const res = await languageService.getAllLanguages(idPath);
                        if (reqId !== activeRequestIdRef.current) return;
                        let projectLangs: any[] = [];
                        if (Array.isArray(res)) {
                            projectLangs = res;
                        } else if (res && typeof res === 'object') {
                            if (Array.isArray(res.data)) {
                                projectLangs = res.data;
                            } else if (res.data && Array.isArray(res.data.data)) {
                                projectLangs = res.data.data;
                            } else if (Array.isArray(res.languages)) {
                                projectLangs = res.languages;
                            }
                        }

                        const candLower = candidateLang.toLowerCase();
                        const matchedLang = Array.isArray(projectLangs) && projectLangs.length > 0 ? projectLangs.find((l: any) => {
                            if (typeof l === 'string') return l.toLowerCase() === candLower;
                            const lId = String(l.id || l.code || l.language_id || l.lang_code || '').toLowerCase();
                            return lId === candLower || 
                                (candLower === 'vn' && (lId === 'vi' || lId === 'vie')) || 
                                (candLower === 'vi' && (lId === 'vn' || lId === 'vie')) ||
                                (candLower === 'en' && lId === 'eng') ||
                                (candLower === 'eng' && lId === 'en') ||
                                (candLower === 'ja' && lId === 'jpn');
                        }) : null;

                        if (matchedLang) {
                            finalLang = (typeof matchedLang === 'object' && matchedLang?.id) ? matchedLang.id : candidateLang;
                        } else {
                            // Nếu API trả về thành công và dự án thực sự không có ngôn ngữ này -> mở ngôn ngữ mặc định của dự án
                            // Ngược lại nếu mảng rỗng do API lỗi hoặc chưa cấu hình -> ưu tiên giữ candidateLang
                            if (Array.isArray(projectLangs) && projectLangs.length > 0) {
                                finalLang = defaultLang || 'vn';
                            } else {
                                finalLang = candidateLang;
                            }
                        }
                    } catch (e) {
                        finalLang = candidateLang;
                    }
                } else {
                    // Truy cập trực tiếp qua thanh địa chỉ URL (không có candidateLang) -> mở theo ngôn ngữ mặc định của dự án
                    finalLang = defaultLang || 'vn';
                }
            } else {
                // Trang sản phẩm tổng
                if (langFromUrl) {
                    finalLang = langFromUrl;
                } else if (langFromCookie) {
                    finalLang = langFromCookie;
                } else if (isRootPath) {
                    finalLang = 'vn';
                } else {
                    finalLang = storedLang || defaultLang || 'vn';
                }
            }

            if (reqId !== activeRequestIdRef.current) return;
            const uiLocale = normalizeUiLocale(finalLang);
            setLocale(uiLocale);
            setRawLanguage(finalLang);
            if (typeof window !== 'undefined') {
                localStorage.setItem('language', finalLang);
                window.dispatchEvent(new CustomEvent('sg3d_language_change', { detail: { locale: uiLocale, rawLanguage: finalLang } }));

                // Dọn dẹp tham số 'lang' khỏi thanh địa chỉ đối với dự án cụ thể nếu được truyền từ query
                if (idPath !== "" && langFromUrl) {
                    const searchParams = new URLSearchParams(window.location.search);
                    if (searchParams.has('lang')) {
                        searchParams.delete('lang');
                        const newSearch = searchParams.toString();
                        const targetUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
                        if (window.history && targetUrl !== (window.location.pathname + window.location.search + window.location.hash)) {
                            window.history.replaceState(null, '', targetUrl);
                        }
                    }
                }
            }
            const msgs = await loadMessages(uiLocale, idPath || undefined);
            if (reqId !== activeRequestIdRef.current) return;
            setMessages(msgs);

            // Xóa cookie 'language' sau khi đã đọc để không ảnh hưởng các lần chuyển trang sau
            if (typeof document !== 'undefined' && langFromCookie) {
                document.cookie = 'language=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
            }
        });
    }, [idPath, isLoading]);

    const getLanguageDefault = async () => {
        if(idPath === "") return 'vn';
        // const langdefault = await projectServices.getSettingByUuid({ id_path: idPath, uuid: "language" });
        const langdefault = await projectServices.getLanguageDefault({ id_path: idPath });
        if (langdefault.success) {
            return langdefault.data.id;
        }
        return 'vn';
    };
    // Khi locale đổi → lưu vào localStorage + load lại messages + cập nhật URL path
    const changeLocale = async (newLocale: string) => {
        const reqId = ++activeRequestIdRef.current;
        const uiLocale = normalizeUiLocale(newLocale);
        setLocale(uiLocale);
        setRawLanguage(newLocale);
        if (typeof window !== 'undefined') {
            localStorage.setItem('language', newLocale);
            window.dispatchEvent(new CustomEvent('sg3d_language_change', { detail: { locale: uiLocale, rawLanguage: newLocale } }));
        }

        // Load messages từ file local
        const newMessages = await loadMessages(uiLocale, idPath || undefined);
        if (reqId !== activeRequestIdRef.current) return;
        setMessages(newMessages);

        // Cập nhật URL path segment để tương thích với cấu hình mới
        if (typeof window !== 'undefined') {
            const pathname = window.location.pathname;
            const segments = pathname.split('/').filter(Boolean);
            let newPath = pathname;

            // Kiểm tra xem có phải là trang dự án cụ thể không
            let isProjectPage = Boolean(idPath) || Boolean((window as any)?.id_path) || Boolean((window as any)?.idProject);
            let cleanSegments = [...segments];
            if (cleanSegments.length > 0 && isLocale(cleanSegments[0])) {
                cleanSegments.shift();
            }
            if (cleanSegments.length >= 1) {
                const firstSegment = cleanSegments[0].toLowerCase();
                const systemSegments = ['admin', 'activate', 'verify', 'reset-password', 'api', '404'];
                if (!systemSegments.includes(firstSegment)) {
                    isProjectPage = true;
                }
            }

            if (isProjectPage) {
                // Đối với dự án cụ thể, không hiển thị/thêm ký hiệu ngôn ngữ trên URL pathname
                // Nếu đang có prefix ngôn ngữ, ta loại bỏ nó đi
                if (segments.length > 0 && isLocale(segments[0])) {
                    newPath = '/' + segments.slice(1).join('/');
                } else {
                    newPath = pathname;
                }

                // Chuẩn hóa đường dẫn gạch chéo
                newPath = newPath.replace(/\/+/g, '/');

                // Lấy các query parameter hiện tại nhưng loại bỏ 'lang'
                const searchParams = new URLSearchParams(window.location.search);
                searchParams.delete('lang');
                const newSearch = searchParams.toString();
                const targetUrl = newPath + (newSearch ? `?${newSearch}` : '');
                
                if (window.history && targetUrl !== (window.location.pathname + window.location.search)) {
                    window.history.replaceState(null, '', targetUrl);
                }
            } else {
                // Đối với trang giới thiệu tổng, giữ nguyên logic thêm prefix ngôn ngữ
                if (segments.length > 0 && isLocale(segments[0])) {
                    // Đã có prefix ngôn ngữ, thay thế hoặc xóa nếu là ngôn ngữ mặc định ('vn')
                    if (uiLocale === 'vn') {
                        newPath = '/' + segments.slice(1).join('/');
                    } else {
                        segments[0] = uiLocale;
                        newPath = '/' + segments.join('/');
                    }
                } else {
                    // Chưa có prefix, thêm vào nếu không phải tiếng Việt ('vn')
                    if (uiLocale !== 'vn') {
                        newPath = '/' + uiLocale + pathname;
                    }
                }
                
                // Xử lý dấu gạch chéo
                newPath = newPath.replace(/\/+/g, '/');
                if (newPath !== pathname) {
                    // Giữ lại query params nếu có và cập nhật URL mà không reload nếu đang ở ứng dụng động
                    const targetUrl = newPath + window.location.search;
                    if (window.history && targetUrl !== (window.location.pathname + window.location.search)) {
                        window.history.replaceState(null, '', targetUrl);
                    }
                }
            }
        }
    };

    const getCurrentLanguage = () => {
        if (Array.isArray(window.languageList)) {
            return window.languageList.find((lang: any) =>
                lang.id === locale ||
                lang.id === rawLanguage ||
                normalizeUiLocale(lang.id) === locale ||
                normalizeUiLocale(lang.code) === locale
            ) || { id: locale, name: locale === 'en' ? 'English' : 'Vietnamese' };
        }
        return { id: locale, name: locale === 'en' ? 'English' : 'Vietnamese' };
    };

    useEffect(() => {
        window.getCurrentLanguage = getCurrentLanguage;
        (window as any).changeLocale = changeLocale;
    }, [locale, rawLanguage]);

    const safeMessages = useMemo(() => unflattenMessages(messages), [messages]);

    return (
        <LanguageContext.Provider value={{ locale, rawLanguage, setLocale: changeLocale }}>
            <NextIntlClientProvider locale={locale} messages={safeMessages} timeZone="Asia/Ho_Chi_Minh">
                {children}
            </NextIntlClientProvider>
        </LanguageContext.Provider>
    );
};
